import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, X, Loader2, Clock, CreditCard, Package, BookOpen } from 'lucide-react';
import axios from 'axios';
import EquipmentRentalModal from './EquipmentRentalModal';

const API_URL = '/api';

// Service slug mapping for SEO URLs
const SERVICE_SLUG_MAP = {
  'Fizioterapevtska obravnava': 'fizioterapija-ljubljana',
  'TECAR terapija': 'tecar-terapija-ljubljana',
  'Tecar terapija': 'tecar-terapija-ljubljana',
  'Visoko intenzivna laser terapija': 'laser-terapija-ljubljana',
  'Laser terapija': 'laser-terapija-ljubljana',
  'Dry needling': 'dry-needling-ljubljana',
  'Suho iglanje': 'dry-needling-ljubljana',
  'Športna masaža celega telesa': 'sportna-masaza-ljubljana',
  'Delna športna masaža': 'sportna-masaza-ljubljana',
  'Terapevtska masaža': 'sportna-masaza-ljubljana',
  'Masaža nosečnic': 'masaza-nosecnic-ljubljana',
  'Terapija nosečnic': 'masaza-nosecnic-ljubljana',
  'Obravnava mamic po porodu': 'masaza-nosecnic-ljubljana',
  'Terapevtska vadba': 'terapevtska-vadba-ljubljana',
  'Kineziotaping': 'kineziotaping-ljubljana',
  'Kinesio taping': 'kineziotaping-ljubljana',
  'Posvet s fizioterapevtom': 'fizioterapija-ljubljana'
};

// Service images mapping
const SERVICE_IMAGES = {
  '1': '/images/fizioterapevtska-obravnava-pacient.jpg',
  '2': 'https://static.wixstatic.com/media/526f7b_ded52b49dc8c423a93884ea801f7fd11~mv2.jpg/v1/fill/w_400,h_240,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/_MI07135-2.jpg',
  '3': 'https://static.wixstatic.com/media/526f7b_50b0b1d13ce04e60ae610e0ae6723c4e~mv2.jpg/v1/fill/w_400,h_240,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Laser%20terapija.jpg',
  '4': '/images/suhe-igle-akupunktura-terapija.avif',
  '5': 'https://static.wixstatic.com/media/526f7b_026103b5969b415ab61d91656d840edf~mv2.jpg/v1/fill/w_400,h_240,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/terapevtska_masa%C3%85%C2%BEa2.jpg',
  '6': 'https://static.wixstatic.com/media/22594fe84d314d61af5adab9dba030db.jpg/v1/fill/w_400,h_240,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Pregnant%20Woman%20Practicing%20Yoga.jpg',
  '7': 'https://static.wixstatic.com/media/526f7b_5c1cf658de4944f39fb4428457758286~mv2.jpg/v1/fill/w_400,h_240,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/_MI07417-2.jpg',
  '8': 'https://static.wixstatic.com/media/526f7b_55f251eefd5a448ba586c564e1480391~mv2.jpg/v1/fill/w_400,h_240,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/_MI07330-2.jpg',
  'default': '/images/fizioterapevtska-obravnava-pacient.jpg'
};

// Full descriptive names for landing page cards
const DISPLAY_NAMES = {
  'Fizioterapevtska obravnava': 'Fizioterapevtska obravnava',
  'TECAR terapija': 'Tecar terapija',
  'Tecar terapija': 'Tecar terapija',
  'Visoko intenzivna laser terapija': 'Laser terapija',
  'Laser terapija': 'Laser terapija',
  'Dry needling': 'Dry needling (suho iglanje)',
  'Suho iglanje': 'Dry needling (suho iglanje)',
  'Športna masaža celega telesa': 'Športna masaža (celo telo)',
  'Delna športna masaža': 'Športna masaža (delna)',
  'Masaža nosečnic': 'Masaža nosečnic',
  'Terapevtska masaža': 'Terapevtska masaža',
  'Terapija nosečnic': 'Fizioterapija nosečnic',
  'Obravnava mamic po porodu': 'Obravnava po porodu',
  'Terapevtska vadba': 'Terapevtska vadba',
  'Kineziotaping': 'Kineziotaping',
  'Kinesio taping': 'Kineziotaping',
  'Posvet s fizioterapevtom': 'Posvet s fizioterapevtom'
};

const ServicesSection = ({ onBookService }) => {
  const [selectedService, setSelectedService] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/services`);
        const enrichedServices = response.data.map(service => ({
          ...service,
          image: service.image || SERVICE_IMAGES[service.id] || SERVICE_IMAGES['default'],
          shortName: DISPLAY_NAMES[service.name] || DISPLAY_NAMES[service.name?.trim()] || service.name
        }));
        setServices(enrichedServices);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedService(null);
        if (window.location.pathname !== '/') {
          window.history.pushState({}, '', '/');
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Handle browser back button to close modal
  useEffect(() => {
    const handlePopState = () => {
      if (selectedService) {
        setSelectedService(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedService]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedService) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedService]);

  if (loading) {
    return (
      <section id="storitve" className="py-10 md:py-16 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-[#f87171] font-semibold tracking-wider uppercase text-sm mb-4">
              Naše storitve
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a365d] mb-4">
              Strokovna fizioterapija
            </h2>
          </div>
          <div className="flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#f87171]" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section
        id="storitve"
        className="py-10 md:py-16 bg-white"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-8">
            <span className="inline-block text-[#f87171] font-semibold tracking-wider uppercase text-sm mb-4">
              Naše storitve
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a365d] mb-4">
              Strokovna fizioterapija
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Izberite storitev za več informacij
            </p>
          </div>

          {/* Services Grid - Compact Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {services
              .filter(s => s.is_active !== false && !s.name.toLowerCase().includes('paket'))
              .map((service) => (
              <button
                key={service.id}
                onClick={() => {
                  setSelectedService(service);
                  const slug = SERVICE_SLUG_MAP[service.name] || SERVICE_SLUG_MAP[service.name?.trim()];
                  if (slug) {
                    window.history.pushState({ modal: true, serviceId: service.id }, '', `/${slug}`);
                  }
                }}
                className="group relative overflow-hidden rounded-2xl aspect-[4/3] transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
                data-testid={`service-card-${service.id}`}
              >
                {/* Background Image */}
                <img loading="lazy" decoding="async"
                  src={service.image}
                  alt={service.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a365d]/90 via-[#1a365d]/40 to-transparent group-hover:from-[#1a365d]/95 transition-all duration-300" />
                
                {/* Content */}
                <div className="absolute inset-0 p-3 md:p-4 flex flex-col justify-end">
                  <h3 className="text-white font-bold text-sm md:text-base leading-tight">
                    {service.shortName}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-white/80 text-xs">{service.duration} min</span>
                    <span className="text-white/60">•</span>
                    <span className="text-[#f87171] font-semibold text-xs">{service.price} €</span>
                  </div>
                </div>

                {/* Hover hint */}
                <div className="absolute top-2 right-2 w-6 h-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="w-3 h-3 text-white" />
                </div>
              </button>
            ))}
          </div>

          {/* Equipment Rental Button */}
          <div className="mt-8 text-center">
            <button
              onClick={() => {
                setShowEquipmentModal(true);
                window.history.pushState({}, '', '/izposoja-fizoterapevtske-opreme-ljubljana');
              }}
              className="inline-flex items-center gap-3 bg-[#1a365d] hover:bg-[#1a365d]/90 text-white px-6 py-3 rounded-full font-semibold transition-all hover:shadow-lg"
              data-testid="equipment-rental-btn"
            >
              <Package className="w-5 h-5" />
              Izposoja opreme
            </button>
            <p className="text-slate-500 text-sm mt-2">Elektrostimulator, magnetna blazina</p>
          </div>
        </div>
      </section>

      {/* Equipment Rental Modal */}
      <EquipmentRentalModal 
        isOpen={showEquipmentModal} 
        onClose={() => {
          setShowEquipmentModal(false);
          window.history.pushState({}, '', '/');
        }} 
      />

      {/* Service Detail Modal */}
      {selectedService && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          onClick={() => {
            setSelectedService(null);
            if (window.location.pathname !== '/') {
              window.history.pushState({}, '', '/');
            }
          }}
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Modal Content */}
          <div className="min-h-full flex items-end md:items-center justify-center p-0 md:p-4">
            <div 
              className="relative w-full md:w-auto md:max-w-2xl bg-white rounded-t-3xl md:rounded-3xl overflow-hidden shadow-2xl animate-slide-up md:animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => {
                  setSelectedService(null);
                  if (window.location.pathname !== '/') {
                    window.history.pushState({}, '', '/');
                  }
                }}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>

            {/* Image */}
            <div className="relative h-48 md:h-64">
              <img loading="lazy" decoding="async"
                src={selectedService.image}
                alt={selectedService.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              
              {/* Price badge */}
              <div className="absolute bottom-4 left-4 bg-[#f87171] text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg">
                {selectedService.price} €
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              <span className="text-[#f87171] font-semibold text-sm uppercase tracking-wider">
                Storitev
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-[#1a365d] mt-1 mb-4">
                {selectedService.name}
              </h3>
              
              <p className="text-slate-600 text-base leading-relaxed mb-6">
                {selectedService.description}
              </p>

              {/* Info pills */}
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-full">
                  <Clock className="w-4 h-4 text-[#1a365d]" />
                  <span className="text-[#1a365d] font-medium text-sm">{selectedService.duration} min</span>
                </div>
                <div className="flex items-center gap-2 bg-[#f87171]/10 px-4 py-2 rounded-full">
                  <CreditCard className="w-4 h-4 text-[#f87171]" />
                  <span className="text-[#f87171] font-medium text-sm">{selectedService.price} €</span>
                </div>
              </div>

              {/* Preberi več link to service page */}
              {SERVICE_SLUG_MAP[selectedService.name] || SERVICE_SLUG_MAP[selectedService.name?.trim()] ? (
                <a
                  href={`/${SERVICE_SLUG_MAP[selectedService.name] || SERVICE_SLUG_MAP[selectedService.name?.trim()]}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedService(null);
                    window.location.href = `/${SERVICE_SLUG_MAP[selectedService.name] || SERVICE_SLUG_MAP[selectedService.name?.trim()]}`;
                  }}
                  className="flex items-center gap-2 text-[#1a365d] hover:text-[#f87171] font-medium text-sm mb-6 transition-colors group"
                  data-testid={`read-more-${selectedService.id}`}
                >
                  <BookOpen className="w-4 h-4" />
                  Preberi več o storitvi
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              ) : null}

              {/* CTA Button */}
              <Button
                onClick={() => {
                  setSelectedService(null);
                  onBookService(selectedService);
                }}
                className="w-full bg-[#f87171] hover:bg-[#ef4444] text-white rounded-xl py-6 font-semibold text-lg shadow-lg shadow-[#f87171]/30"
                data-testid={`book-service-${selectedService.id}`}
              >
                Rezerviraj termin
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Add animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes fade-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default ServicesSection;
